import { useEffect, useMemo } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { createArrivalSchedule } from '#/api/srm/purchaseOrder/arrivalSchedule/create';
import { updateArrivalSchedule } from '#/api/srm/purchaseOrder/arrivalSchedule/update';
import { fetchPurchaseOrderArrivalScheduleDetail } from '#/api/srm/purchaseOrder/fetchArrivalScheduleDetail';
import { fetchPurchaseOrderDetail } from '#/api/srm/purchaseOrder/fetchDetail';
import { Button } from '#/modules/commonComponents/button';
import FloatingDateInput from '#/modules/commonComponents/form/FloatingDateInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import PageShell from '#/modules/infra/layout/PageShell';
import ArrivalScheduleLinesEditor from '#/modules/srmMgmt/purchaseOrder/components/ArrivalScheduleLinesEditor';
import ArrivalScheduleStatusBadge from '#/modules/srmMgmt/purchaseOrder/components/ArrivalScheduleStatusBadge';
import {
  getPurchaseOrderApiErrorMessage,
  isPurchaseOrderVersionConflict,
} from '#/modules/srmMgmt/purchaseOrder/helper/apiError';
import {
  canEditDraftArrivalSchedule,
} from '#/modules/srmMgmt/purchaseOrder/helper/arrivalScheduleStatus';
import {
  buildArrivalScheduleCreateDefaultValues,
  buildArrivalScheduleEditDefaultValues,
  buildArrivalScheduleWritePayload,
  resolveArrivalScheduleStatusOnCreate,
} from '#/modules/srmMgmt/purchaseOrder/helper/mapArrivalScheduleForm';
import {
  canCreateArrivalSchedule,
} from '#/modules/srmMgmt/purchaseOrder/helper/purchaseOrderStatus';
import {
  ARRIVAL_SCHEDULE_FIELD_LABELS,
  arrivalScheduleFormSchema,
} from '#/modules/srmMgmt/purchaseOrder/schemas/arrivalScheduleSchema';
import { toast } from '#/utils/toast';
import {
  purchaseOrderArrivalScheduleDetailQueryKey,
  purchaseOrderArrivalScheduleListQueryKeyPrefix,
  purchaseOrderDetailQueryKey,
} from '#/utils/queryKeys';

const EMPTY_FORM = {
  expectedArrivalDate: '',
  remark: '',
  lines: [],
};

const CreateEditArrivalSchedulePage = () => {
  const { id: purchaseOrderId, scheduleId } = useParams();
  const isEdit = Boolean(scheduleId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const poDetailQuery = useQuery({
    queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    queryFn: () => fetchPurchaseOrderDetail(purchaseOrderId),
    enabled: Boolean(purchaseOrderId),
  });

  const scheduleDetailQuery = useQuery({
    queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
      purchaseOrderId,
      scheduleId,
    ),
    queryFn: () => fetchPurchaseOrderArrivalScheduleDetail(
      purchaseOrderId,
      scheduleId,
    ),
    enabled: isEdit && Boolean(purchaseOrderId) && Boolean(scheduleId),
  });

  const poDetail = poDetailQuery.data?.data;
  const schedule = scheduleDetailQuery.data?.data;

  const defaultValues = useMemo(() => {
    if (isEdit) {
      if (!schedule) {
        return EMPTY_FORM;
      }
      return buildArrivalScheduleEditDefaultValues(schedule, poDetail);
    }
    return buildArrivalScheduleCreateDefaultValues(poDetail);
  }, [isEdit, schedule, poDetail]);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(arrivalScheduleFormSchema),
    defaultValues: EMPTY_FORM,
  });

  useEffect(() => {
    if (isEdit) {
      if (!schedule) {
        return;
      }
      resetValidationSummary();
      reset(defaultValues);
      return;
    }
    if (!poDetail) {
      return;
    }
    resetValidationSummary();
    reset(defaultValues);
  }, [isEdit, schedule, poDetail, defaultValues, reset, resetValidationSummary]);

  async function invalidateAfterWrite() {
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderDetailQueryKey(purchaseOrderId),
    });
    await queryClient.invalidateQueries({
      queryKey: purchaseOrderArrivalScheduleListQueryKeyPrefix(purchaseOrderId),
    });
    if (isEdit && scheduleId) {
      await queryClient.invalidateQueries({
        queryKey: purchaseOrderArrivalScheduleDetailQueryKey(
          purchaseOrderId,
          scheduleId,
        ),
      });
    }
  }

  const saveMutation = useMutation({
    mutationFn: ({ values, updatedAt }) => {
      if (isEdit) {
        return updateArrivalSchedule(
          purchaseOrderId,
          scheduleId,
          buildArrivalScheduleWritePayload(values, { updatedAt }),
        );
      }
      return createArrivalSchedule(
        purchaseOrderId,
        buildArrivalScheduleWritePayload(values),
      );
    },
    onSuccess: async (result) => {
      toast.success(result?.message || (isEdit ? '排程已更新' : '排程已建立'));
      await invalidateAfterWrite();
      navigate(ROUTES.purchaseOrderDetail(purchaseOrderId));
    },
    onError: (error) => {
      toast.error(getPurchaseOrderApiErrorMessage(
        error,
        isEdit ? '更新排程失敗' : '建立排程失敗',
      ));
      if (isEdit && isPurchaseOrderVersionConflict(error)) {
        scheduleDetailQuery.refetch();
      }
    },
  });

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (isEdit) {
      if (!canEditDraftArrivalSchedule(schedule?.status)) {
        toast.error('僅草稿排程可編輯');
        return;
      }
      const updatedAt = schedule?.record?.updatedAt ?? schedule?.updatedAt ?? null;
      if (!updatedAt) {
        toast.error('缺少 updatedAt，請重新載入後再試');
        return;
      }
      saveMutation.mutate({ values, updatedAt });
      return;
    }
    if (!canCreateArrivalSchedule(poDetail?.status)) {
      toast.error('目前物料採購單狀態不可新建排程');
      return;
    }
    saveMutation.mutate({ values });
  }

  const isLoading = poDetailQuery.isLoading
    || (isEdit && scheduleDetailQuery.isLoading);
  const isError = poDetailQuery.isError
    || (isEdit && scheduleDetailQuery.isError);
  const error = poDetailQuery.error || scheduleDetailQuery.error;

  const canCreate = canCreateArrivalSchedule(poDetail?.status);
  const canEdit = canEditDraftArrivalSchedule(schedule?.status);
  const createStatusHint = resolveArrivalScheduleStatusOnCreate(poDetail?.status);

  if (!isLoading && !isError && poDetail && !isEdit && !canCreate) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  if (!isLoading && !isError && isEdit && schedule && !canEdit) {
    return <Navigate to={ROUTES.purchaseOrderDetail(purchaseOrderId)} replace />;
  }

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="w-full">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">
                {isEdit ? '編輯交期排程' : '新建交期排程'}
              </span>
              {isEdit && schedule?.status ? (
                <ArrivalScheduleStatusBadge status={schedule.status} size="sm" />
              ) : null}
              {!isEdit && createStatusHint ? (
                <ArrivalScheduleStatusBadge status={createStatusHint} size="sm" />
              ) : null}
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>返回詳情</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getPurchaseOrderApiErrorMessage(error, '載入失敗')}
            </p>
          ) : null}

          {!isLoading && !isError && poDetail && (!isEdit || schedule) ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                物料採購單
                {' '}
                {poDetail.purchaseOrderNo || purchaseOrderId}
                {' · '}
                供應商
                {' '}
                {poDetail.supplier?.name || poDetail.supplier?.code || '—'}
                {!isEdit && createStatusHint ? (
                  <>
                    {' · '}
                    建立後狀態
                    {' '}
                    {createStatusHint}
                  </>
                ) : null}
              </p>

              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={ARRIVAL_SCHEDULE_FIELD_LABELS}
                customMessages={customMessages}
              />

              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-6">
                    <div className="form-block">
                      <h2 className="form-block__title">排程資訊</h2>
                    </div>
                    <div
                      className={cn(
                        'grid grid-cols-1 gap-3',
                        '@4xl/sheet:grid-cols-2',
                      )}
                    >
                      <FloatingDateInput
                        id="expectedArrivalDate"
                        label="預計到貨日"
                        control={control}
                        errors={errors}
                        placeholder="西元年/月/日"
                      />
                      <FloatingTextarea
                        id="remark"
                        label="備註"
                        register={register}
                        errors={errors}
                        rows={2}
                        containerClassName="@4xl/sheet:col-span-2"
                      />
                    </div>
                  </section>

                  <ArrivalScheduleLinesEditor
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.purchaseOrderDetail(purchaseOrderId)}>取消</Link>
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      saveMutation.isPending
                      || (isEdit ? !canEdit : !canCreate)
                    }
                  >
                    {saveMutation.isPending
                      ? (isEdit ? '儲存中…' : '建立中…')
                      : (isEdit ? '儲存' : '建立排程')}
                  </Button>
                </div>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditArrivalSchedulePage;
